import { APP_INITIALIZER, inject, Provider } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { AuthService } from '../../../chat-common/src/lib/services/auth.service';
import { storeAppOrigin } from './app-origin';

/**
 * Reads the URL fragment: `#token=<accessCode>&origin=<origin>` (both optional), or just `#<accessCode>`.
 */
function parseFragment(hash: string): { accessCode: string | null; origin: string | null } {
  const fragment = hash.replace(/^#/, '');
  if (!fragment) return { accessCode: null, origin: null };
  if (!fragment.includes('=')) return { accessCode: fragment, origin: null };
  const params = new URLSearchParams(fragment);
  return { accessCode: params.get('token'), origin: params.get('origin') };
}

/**
 * Any page can be opened with `#token=<accessCode>` and/or `#origin=<origin>`. Remembers the origin
 * and logs the user in before the router's initial navigation, so every route's guards already see
 * the session, then drops the fragment.
 */
function loginFromFragment(): () => Promise<void> {
  const authService = inject(AuthService);
  return async () => {
    const { accessCode, origin } = parseFragment(window.location.hash);
    if (!accessCode && !origin) return;

    history.replaceState(history.state, '', window.location.pathname + window.location.search);
    if (origin) storeAppOrigin(origin);
    if (!accessCode) return;

    try {
      // Bounded so a stuck request can never block app startup
      await firstValueFrom(authService.loginWithAccessCode(accessCode).pipe(timeout(15000)));
    } catch (e) {
      // Guards send the user to login as usual
      console.error('Access code login failed', e);
    }
  };
}

export const accessCodeFragmentInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: loginFromFragment,
  multi: true,
};
